/**
 * Markdown Documentation Export Handler
 * Generates comprehensive documentation from diagram data
 */

import { Model, ModelItem, Connector, Rectangle, TextBox, View } from '../../../types';
import { ElementGroup } from '@/features/groups/groupTypes';
import { iconPackManager } from '@features/components/iconPacks';
import {
    ExportHandler,
    ExportResult,
    ExportConfig,
    ExportProgressCallback,
} from '../exportTypes';
import {
    generateFilename,
    createExportMetadata,
} from '../exportUtils';

// Extended model type that includes groups
interface ExtendedModel extends Model {
    groups?: ElementGroup[];
}

export class MarkdownExportHandler implements ExportHandler {
    public readonly format = 'markdown' as const;
    private readonly APP_VERSION = '1.0.0';

    async export(
        element: HTMLElement,
        model: Model,
        config: ExportConfig,
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult> {
        try {
            onProgress?.(0, 'Preparing documentation...');

            onProgress?.(30, 'Analyzing diagram structure...');

            // Generate markdown content with diagram URL
            const markdown = this.generateMarkdown(model);

            onProgress?.(90, 'Finalizing documentation...');

            const filename = config.filename || generateFilename(model.title || 'flowi-diagram', 'markdown');
            const rect = element.getBoundingClientRect();
            const metadata = createExportMetadata(
                config,
                rect.width,
                rect.height,
                model.title
            );

            onProgress?.(100, 'Documentation complete!');

            return {
                success: true,
                data: markdown,
                filename,
                format: 'json', // Use json format type for text download
                metadata,
            };
        } catch (error) {
            return {
                success: false,
                filename: '',
                error: error as Error,
            };
        }
    }

    /**
     * Generate comprehensive arc42/C4-style markdown documentation
     */
    private generateMarkdown(model: Model): string {
        const sections: string[] = [];

        // Header
        sections.push(this.generateHeader(model));

        // Table of Contents
        sections.push(this.generateTableOfContents());

        // 1. Introduction and Goals (arc42 Section 1)
        sections.push(this.generateIntroductionAndGoals(model));

        // 2. Architecture Constraints (arc42 Section 2)
        sections.push(this.generateArchitectureConstraints(model));

        // 3. Context and Scope (arc42 Section 3 / C4 Context)
        sections.push(this.generateContextAndScope(model));

        // 4. Solution Strategy (arc42 Section 4)
        sections.push(this.generateSolutionStrategy(model));

        // Diagram Link
        sections.push(this.generateDiagramSection());

        // Mermaid Diagrams (Interactive)
        sections.push(this.generateMermaidDiagrams(model));

        // 5. Building Block View (arc42 Section 5 / C4 Container/Component)
        sections.push(this.generateBuildingBlockView(model));

        // 6. Runtime View (arc42 Section 6)
        sections.push(this.generateRuntimeView(model));

        // 7. Deployment View (arc42 Section 7)
        sections.push(this.generateDeploymentView(model));

        // 8. Cross-cutting Concepts (arc42 Section 8)
        sections.push(this.generateCrossCuttingConcepts(model));

        // 9. Architecture Decisions (arc42 Section 9)
        sections.push(this.generateArchitectureDecisions(model));

        // 10. Quality Requirements (arc42 Section 10)
        sections.push(this.generateQualityRequirements(model));

        // 11. Risks and Technical Debt (arc42 Section 11)
        sections.push(this.generateRisksAndTechnicalDebt(model));

        // Technical Reference
        sections.push(this.generateTechnicalReference(model));

        // Metadata
        sections.push(this.generateMetadataSection(model));

        return sections.filter(s => s.length > 0).join('\n\n---\n\n');
    }

    /**
     * Generate document header
     */
    private generateHeader(model: Model): string {
        const title = model.title || 'Untitled Diagram';
        const description = model.description || '';

        return `# ${title}

${description ? `> ${description}\n` : ''}
**Generated:** ${new Date().toLocaleString()}
**Version:** ${model.version || 'N/A'}`;
    }

    /**
     * Generate table of contents (arc42/C4 structure)
     */
    private generateTableOfContents(): string {
        const sections = [
            '- [1. Introduction and Goals](#1-introduction-and-goals)',
            '- [2. Architecture Constraints](#2-architecture-constraints)',
            '- [3. Context and Scope](#3-context-and-scope)',
            '- [4. Solution Strategy](#4-solution-strategy)',
            '- [Architecture Diagram](#architecture-diagram)',
            '- [5. Building Block View](#5-building-block-view)',
            '- [6. Runtime View](#6-runtime-view)',
            '- [7. Deployment View](#7-deployment-view)',
            '- [8. Cross-cutting Concepts](#8-cross-cutting-concepts)',
            '- [9. Architecture Decisions](#9-architecture-decisions)',
            '- [10. Quality Requirements](#10-quality-requirements)',
            '- [11. Risks and Technical Debt](#11-risks-and-technical-debt)',
            '- [Technical Reference](#technical-reference)',
            '- [Metadata](#metadata)',
        ];

        return `## Table of Contents

${sections.join('\n')}`;
    }

    /**
     * arc42 Section 1: Introduction and Goals
     */
    private generateIntroductionAndGoals(model: Model): string {
        const items = model.items || [];
        const connectors = this.getConnectors(model);

        // Infer system type from components
        const systemType = this.inferSystemType(items);
        const keyComponents = this.identifyKeyComponents(model);

        return `## 1. Introduction and Goals

### 1.1 Requirements Overview

${model.description || '*System purpose and requirements should be documented here.*'}

### 1.2 Quality Goals

The architecture prioritizes the following quality attributes:

1. **Scalability** - System designed to handle growing workload
2. **Maintainability** - Clear component separation and defined interfaces
3. **Reliability** - ${connectors.length} defined integration points ensure robust communication

### 1.3 Stakeholders

| Role | Expectations | Concerns |
|------|--------------|----------|
| **Development Team** | Clear architecture, maintainable code | Technical debt, complexity |
| **Operations Team** | Deployability, monitoring | System reliability, performance |
| **Business Stakeholders** | Feature delivery, time to market | Cost, scalability |
| **End Users** | Performance, availability | Downtime, data security |

### 1.4 System Overview

This is a **${systemType}** system comprising ${items.length} components organized into a ${this.analyzeLayers(model).length > 0 ? 'layered' : 'distributed'} architecture.

**Key System Components:**
${keyComponents.map(c => `- **${c.name}** - ${c.description || 'Core system component'}`).join('\n')}`;
    }

    /**
     * arc42 Section 2: Architecture Constraints
     */
    private generateArchitectureConstraints(model: Model): string {
        const tech = this.analyzeTechnologyStack(model);

        return `## 2. Architecture Constraints

### 2.1 Technical Constraints

| Constraint | Description | Impact |
|------------|-------------|--------|
| **Technology Stack** | ${tech.categories.join(', ') || 'Various technologies'} | Affects component design and integration |
| **Integration** | ${this.getConnectors(model).length} integration points | Requires robust error handling and monitoring |
| **Data Flow** | ${this.analyzeDataFlow(model, this.getConnectors(model)).entryPoints.length} entry points | Input validation and security at boundaries |

### 2.2 Organizational Constraints

- **Team Structure** - Architecture should support independent team development
- **Release Cycles** - Component independence enables continuous deployment
- **Documentation** - Architecture decisions must be documented and maintained

### 2.3 Conventions

- **Naming** - Components follow consistent naming conventions
- **Communication** - All component interactions are explicitly defined
- **Error Handling** - Standardized error propagation across integration points`;
    }

    /**
     * arc42 Section 3: Context and Scope (C4 Context Diagram)
     */
    private generateContextAndScope(model: Model): string {
        const flowAnalysis = this.analyzeDataFlow(model, this.getConnectors(model));
        const items = model.items || [];

        return `## 3. Context and Scope

### 3.1 Business Context

**System Boundary:**

This system consists of ${items.length} components that work together to deliver the required functionality.

**External Interfaces:**

${flowAnalysis.entryPoints.length > 0 ? `
**Input Interfaces (Entry Points):**
${flowAnalysis.entryPoints.map(ep => `- ${ep} - Receives external requests/data`).join('\n')}
` : ''}

${flowAnalysis.exitPoints.length > 0 ? `
**Output Interfaces (Exit Points):**
${flowAnalysis.exitPoints.map(ep => `- ${ep} - Produces system outputs`).join('\n')}
` : ''}

${flowAnalysis.hubs.length > 0 ? `
**Integration Hubs:**
${flowAnalysis.hubs.map(hub => `- ${hub.name} - Central integration point (${hub.incoming} inputs, ${hub.outgoing} outputs)`).join('\n')}
` : ''}

### 3.2 Technical Context

**Communication Patterns:**

The system uses ${this.getConnectors(model).length} defined communication channels between components.

**Data Flow:**

- **Entry Points:** ${flowAnalysis.entryPoints.length} external inputs
- **Exit Points:** ${flowAnalysis.exitPoints.length} system outputs
- **Internal Integrations:** ${this.getConnectors(model).length - flowAnalysis.entryPoints.length - flowAnalysis.exitPoints.length} internal connections`;
    }

    /**
     * arc42 Section 4: Solution Strategy
     */
    private generateSolutionStrategy(model: Model): string {
        const tech = this.analyzeTechnologyStack(model);
        const layers = this.analyzeLayers(model);

        return `## 4. Solution Strategy

### 4.1 Technology Decisions

**Primary Technology Categories:**

${tech.categories.map(cat => `- **${cat}**`).join('\n') || '- *Technology stack to be documented*'}

**Key Technologies in Use:**

${tech.technologies.slice(0, 10).map(t => `- ${t.name} (${t.count} instances)`).join('\n') || '- *Component technologies to be documented*'}

### 4.2 Architectural Approach

${layers.length > 0 ? `
**Layered Architecture:**

The system is organized into ${layers.length} logical layers:

${layers.map((layer, i) => `${i + 1}. **${layer}** - Logical grouping of related components`).join('\n')}

This layering provides:
- Clear separation of concerns
- Independent scalability of layers
- Simplified testing and maintenance
` : `
**Component-Based Architecture:**

- Loosely coupled components
- Well-defined interfaces
- Independent deployment capability
`}

### 4.3 Key Architecture Decisions

1. **Component Decomposition** - System split into ${model.items?.length || 0} discrete components for modularity
2. **Integration Strategy** - ${this.getConnectors(model).length} explicit integration points enable clear communication
3. **Deployment Model** - ${this.getRectangles(model).length > 0 ? 'Zone-based deployment with logical groupings' : 'Distributed deployment model'}`;
    }

    /**
     * arc42 Section 5: Building Block View (C4 Container/Component)
     */
    private generateBuildingBlockView(model: Model): string {
        const extendedModel = model as ExtendedModel;
        const hierarchy = this.buildHierarchy(extendedModel);
        const sections: string[] = ['## 5. Building Block View'];

        sections.push('### 5.1 Hierarchical Structure');
        sections.push('');
        sections.push('*Components are organized by their logical/physical boundaries (rectangles) and groups.*');
        sections.push('');

        // Rectangles (Zones)
        if (hierarchy.rectangles.length > 0) {
            sections.push('#### Zones / Boundaries');
            sections.push('');

            hierarchy.rectangles.forEach((rect, rectIdx) => {
                const zoneName = rect.rectangle.description || `Zone ${rectIdx + 1}`;
                const totalComponents = rect.groups.reduce((sum, g) => sum + g.items.length, 0) + rect.ungroupedItems.length;

                sections.push(`**${zoneName}** (${totalComponents} components)`);
                sections.push('');

                // Groups within this rectangle (handle hierarchical groups)
                if (rect.groups.length > 0) {
                    const groupTree = this.buildGroupTree(rect.groups.map(g => g.group));
                    const rootGroups = groupTree.get('root') || [];

                    const renderGroup = (group: ElementGroup, depth: number) => {
                        const indent = '  '.repeat(depth);
                        const groupColor = group.color ? ` 🏷️ ${group.color}` : '';
                        const groupData = rect.groups.find(g => g.group.id === group.id);
                        if (!groupData) return;

                        sections.push(`${indent}- **Group: ${group.name}**${groupColor} (${groupData.items.length} components)`);

                        // Render child groups
                        const children = groupTree.get(group.id) || [];
                        children.forEach(child => renderGroup(child, depth + 1));

                        // Render items in this group
                        groupData.items.forEach(item => {
                            const icon = item.icon ? this.getIconName(item.icon) : '';
                            const desc = item.description ? ` - ${item.description}` : '';
                            sections.push(`${indent}  - ${item.name}${icon ? ` [${icon}]` : ''}${desc}`);
                        });

                        sections.push('');
                    };

                    rootGroups.forEach(group => renderGroup(group, 0));
                }

                // Ungrouped items in this rectangle
                if (rect.ungroupedItems.length > 0) {
                    sections.push(`- **Individual Components** (${rect.ungroupedItems.length})`);
                    rect.ungroupedItems.forEach(item => {
                        const icon = item.icon ? this.getIconName(item.icon) : '';
                        const desc = item.description ? ` - ${item.description}` : '';
                        sections.push(`  - ${item.name}${icon ? ` [${icon}]` : ''}${desc}`);
                    });
                    sections.push('');
                }
            });
        }

        // Orphan Groups (not in any rectangle) - Handle hierarchical groups
        if (hierarchy.orphanGroups.length > 0) {
            sections.push('#### Independent Groups');
            sections.push('');
            sections.push('*Groups not contained within any zone/boundary*');
            sections.push('');

            const orphanGroupTree = this.buildGroupTree(hierarchy.orphanGroups.map(g => g.group));
            const rootOrphanGroups = orphanGroupTree.get('root') || [];

            const renderOrphanGroup = (group: ElementGroup, depth: number) => {
                const indent = '  '.repeat(depth);
                const groupColor = group.color ? ` 🏷️ ${group.color}` : '';
                const groupData = hierarchy.orphanGroups.find(g => g.group.id === group.id);
                if (!groupData) return;

                sections.push(`${indent}- **${group.name}**${groupColor} (${groupData.items.length} components)`);

                // Render child groups
                const children = orphanGroupTree.get(group.id) || [];
                children.forEach(child => renderOrphanGroup(child, depth + 1));

                // Render items in this group
                groupData.items.forEach(item => {
                    const icon = item.icon ? this.getIconName(item.icon) : '';
                    const desc = item.description ? ` - ${item.description}` : '';
                    sections.push(`${indent}  - ${item.name}${icon ? ` [${icon}]` : ''}${desc}`);
                });

                sections.push('');
            };

            rootOrphanGroups.forEach(group => renderOrphanGroup(group, 0));
        }

        // Orphan Items (not in any rectangle or group)
        if (hierarchy.orphanItems.length > 0) {
            sections.push('#### Standalone Components');
            sections.push('');
            sections.push('*Components not in any zone or group*');
            sections.push('');

            hierarchy.orphanItems.forEach(item => {
                const icon = item.icon ? this.getIconName(item.icon) : '';
                const desc = item.description ? ` - ${item.description}` : '';
                sections.push(`- ${item.name}${icon ? ` [${icon}]` : ''}${desc}`);
            });
            sections.push('');
        }

        // Component Interactions
        sections.push('### 5.2 Component Interactions');
        sections.push('');
        sections.push(this.generateConnectionsSection(model));

        return sections.join('\n');
    }

    /**
     * arc42 Section 6: Runtime View (Scenarios)
     */
    private generateRuntimeView(model: Model): string {
        const flowAnalysis = this.analyzeDataFlow(model, this.getConnectors(model));
        const connectors = this.getConnectors(model);

        return `## 6. Runtime View

### 6.1 Primary Scenarios

${flowAnalysis.entryPoints.length > 0 ? `
**Scenario 1: Request Processing Flow**

Starting from entry point: ${flowAnalysis.entryPoints[0] || 'External Request'}

${this.generateRequestFlow(model, flowAnalysis.entryPoints[0])}
` : ''}

### 6.2 Data Flow Patterns

The system implements the following data flow patterns:

- **Entry Points:** ${flowAnalysis.entryPoints.length} external data sources
- **Processing Hubs:** ${flowAnalysis.hubs.length} central processing components
- **Exit Points:** ${flowAnalysis.exitPoints.length} output destinations

### 6.3 Integration Scenarios

${connectors.slice(0, 5).map((conn, i) => {
    const { source, target } = this.getConnectorEndpoints(model, conn);
    return `**Integration ${i + 1}:** ${source} → ${target}
- **Description:** ${conn.description || 'Component integration'}
- **Type:** ${conn.style || 'SOLID'} connection`;
}).join('\n\n')}`;
    }

    /**
     * arc42 Section 7: Deployment View
     */
    private generateDeploymentView(model: Model): string {
        const zones = this.getRectangles(model);
        const items = model.items || [];

        return `## 7. Deployment View

### 7.1 Infrastructure Overview

${zones.length > 0 ? `
**Deployment Zones:**

The system is deployed across ${zones.length} logical zones:

${zones.map((zone, i) => `
**Zone ${i + 1}: ${zone.description || `Zone ${i + 1}`}**
- **Purpose:** ${zone.description || 'Logical deployment boundary'}
- **Components:** Multiple services deployed in this zone
`).join('\n')}
` : `
**Deployment Strategy:**

- ${items.length} components deployed across infrastructure
- Distributed deployment model
- Scalable and resilient architecture
`}

### 7.2 Deployment Considerations

- **Scalability:** Components can be scaled independently
- **Availability:** Distributed deployment ensures high availability
- **Monitoring:** All components should be monitored for health and performance
- **Security:** Network segmentation and access controls between zones`;
    }

    /**
     * arc42 Section 8: Cross-cutting Concepts
     */
    private generateCrossCuttingConcepts(model: Model): string {
        return `## 8. Cross-cutting Concepts

### 8.1 Domain Model

The system operates on a domain model with ${model.items?.length || 0} core entities representing the business domain.

### 8.2 Security & Authentication

- **Access Control:** Component-level access control implementation
- **Data Protection:** Secure communication between ${this.getConnectors(model).length} integration points
- **Authentication:** Entry point authentication and authorization

### 8.3 Error Handling

- **Resilience:** Graceful degradation across component boundaries
- **Monitoring:** Comprehensive logging at all integration points
- **Recovery:** Automated recovery mechanisms for failed components

### 8.4 Communication Patterns

- **Synchronous Communication:** ${this.getConnectors(model).filter(c => c.style === 'SOLID').length} direct connections
- **Asynchronous Communication:** ${this.getConnectors(model).filter(c => c.style !== 'SOLID').length} queued/async connections
- **Event-Driven:** Event propagation through component network

### 8.5 Performance

- **Caching:** Strategic caching at integration hubs
- **Load Balancing:** Distribution across ${this.analyzeDataFlow(model, this.getConnectors(model)).hubs.length} hub components
- **Optimization:** Critical path optimization for high-traffic flows`;
    }

    /**
     * arc42 Section 9: Architecture Decisions
     */
    private generateArchitectureDecisions(model: Model): string {
        return `## 9. Architecture Decisions

### ADR 1: Component Architecture

**Status:** Accepted

**Context:**
System requires modularity, independent scaling, and team autonomy.

**Decision:**
Adopt component-based architecture with ${model.items?.length || 0} independent components.

**Consequences:**
- ✅ Independent deployment and scaling
- ✅ Team autonomy and parallel development
- ⚠️ Requires robust integration testing
- ⚠️ Increased operational complexity

### ADR 2: Integration Strategy

**Status:** Accepted

**Context:**
Components need to communicate reliably and efficiently.

**Decision:**
Define ${this.getConnectors(model).length} explicit integration points with clear contracts.

**Consequences:**
- ✅ Clear component boundaries
- ✅ Testable integration points
- ⚠️ Integration overhead
- ⚠️ Potential performance bottlenecks

${this.getRectangles(model).length > 0 ? `
### ADR 3: Logical Deployment Zones

**Status:** Accepted

**Context:**
System requires security boundaries and logical organization.

**Decision:**
Organize components into ${this.getRectangles(model).length} logical deployment zones.

**Consequences:**
- ✅ Security through network segmentation
- ✅ Clear operational boundaries
- ⚠️ Cross-zone communication overhead
` : ''}`;
    }

    /**
     * arc42 Section 10: Quality Requirements
     */
    private generateQualityRequirements(model: Model): string {
        return `## 10. Quality Requirements

### 10.1 Quality Tree

\`\`\`
Quality
├── Performance
│   ├── Response Time: < 200ms for critical paths
│   ├── Throughput: Handle peak load with ${this.analyzeDataFlow(model, this.getConnectors(model)).hubs.length} processing hubs
│   └── Resource Efficiency: Optimal resource utilization per component
├── Reliability
│   ├── Availability: 99.9% uptime target
│   ├── Fault Tolerance: Graceful degradation
│   └── Recoverability: Automated recovery mechanisms
├── Security
│   ├── Authentication: Secured entry points
│   ├── Authorization: Component-level access control
│   └── Data Protection: Encrypted communication
└── Maintainability
    ├── Modularity: ${model.items?.length || 0} independent components
    ├── Testability: Clear component boundaries
    └── Deployability: Independent deployment capability
\`\`\`

### 10.2 Quality Scenarios

**Performance Scenario:**
- **Scenario:** User request through entry point
- **Expected:** < 200ms response time
- **Measurement:** End-to-end latency monitoring

**Reliability Scenario:**
- **Scenario:** Component failure
- **Expected:** System continues with degraded functionality
- **Measurement:** Error rate monitoring, circuit breaker metrics

**Security Scenario:**
- **Scenario:** Unauthorized access attempt
- **Expected:** Request rejected at entry point
- **Measurement:** Authentication failure logs`;
    }

    /**
     * arc42 Section 11: Risks and Technical Debt
     */
    private generateRisksAndTechnicalDebt(model: Model): string {
        const connectors = this.getConnectors(model);

        return `## 11. Risks and Technical Debt

### 11.1 Risk Assessment

| Risk | Probability | Impact | Mitigation |
|------|------------|--------|------------|
| **Integration Failures** | Medium | High | Circuit breakers, retry logic, monitoring |
| **Performance Bottlenecks** | Medium | Medium | Load testing, caching, horizontal scaling |
| **Security Vulnerabilities** | Low | High | Regular security audits, penetration testing |
| **Component Dependencies** | Medium | Medium | Loose coupling, versioned APIs |

### 11.2 Technical Debt

- **Documentation:** Architecture decisions and component interactions need ongoing documentation
- **Testing:** Integration testing coverage for ${connectors.length} integration points
- **Monitoring:** Comprehensive observability across all components
- **Automation:** CI/CD pipeline for ${model.items?.length || 0} components

### 11.3 Improvement Opportunities

1. **Enhanced Monitoring:** Distributed tracing across all integration points
2. **Performance Optimization:** Profile and optimize critical data flows
3. **Security Hardening:** Regular security assessments and updates
4. **Documentation:** Maintain living documentation aligned with code changes`;
    }

    /**
     * Technical Reference Section (combining previous sections)
     */
    private generateTechnicalReference(model: Model): string {
        const sections: string[] = ['## Technical Reference'];

        // Text Annotations
        const textBoxSection = this.generateTextBoxesSection(model);
        if (textBoxSection) {
            sections.push(textBoxSection);
        }

        // Views
        if (model.views && model.views.length > 1) {
            sections.push(this.generateViewsSection(model));
        }

        // Icons
        sections.push(this.generateIconsSection(model));

        // Colors
        sections.push(this.generateColorsSection(model));

        return sections.join('\n\n');
    }

    /**
     * Generate diagram link section
     */
    private generateDiagramSection(): string {
        // Get current diagram URL from browser location
        const diagramUrl = typeof window !== 'undefined' ? window.location.href : '';

        if (!diagramUrl) {
            return `## Architecture Diagram

*View the live diagram in Flowi to see the full architecture visualization.*`;
        }

        return `## Architecture Diagram

**[🔗 View Interactive Diagram in Flowi](${diagramUrl})**

*Click the link above to view and interact with the full architecture diagram.*`;
    }

    /**
     * Generate connections section
     */
    private generateConnectionsSection(model: Model): string {
        const connectors = this.getConnectors(model);

        if (connectors.length === 0) {
            return '';
        }

        const lines: string[] = ['## Connections & Data Flow'];
        lines.push('');
        lines.push('### Connection Overview');
        lines.push('');
        lines.push(`Total connections: **${connectors.length}**`);
        lines.push('');
        lines.push('| # | Source | Target | Type | Direction | Flow | Description |');
        lines.push('|---|--------|--------|------|-----------|------|-------------|');

        connectors.forEach((connector, index) => {
            const { source, target } = this.getConnectorEndpoints(model, connector);
            const style = connector.style || 'SOLID';
            const arrowType = connector.arrowType || 'SINGLE';
            const arrowDir = connector.arrowDirection || 'forward';
            const flowIndicator = this.getFlowIndicator(connector);
            const label = connector.description || '*No description*';

            const directionSymbol = arrowDir === 'backward' ? '⬅️' : '➡️';
            const arrowSymbol = arrowType === 'DOUBLE' ? '⬌' : arrowType === 'NONE' ? '━' : '→';

            lines.push(`| ${index + 1} | ${source} | ${target} | ${style} | ${arrowSymbol} ${directionSymbol} | ${flowIndicator || '-'} | ${label} |`);
        });

        // Analyze data flows
        lines.push('');
        lines.push('### Data Flow Analysis');
        lines.push('');

        const flowAnalysis = this.analyzeDataFlow(model, connectors);
        if (flowAnalysis.entryPoints.length > 0) {
            lines.push('#### Entry Points');
            flowAnalysis.entryPoints.forEach(itemName => {
                lines.push(`- ${itemName}`);
            });
            lines.push('');
        }

        if (flowAnalysis.exitPoints.length > 0) {
            lines.push('#### Exit Points');
            flowAnalysis.exitPoints.forEach(itemName => {
                lines.push(`- ${itemName}`);
            });
            lines.push('');
        }

        if (flowAnalysis.hubs.length > 0) {
            lines.push('#### Integration Hubs');
            lines.push('');
            flowAnalysis.hubs.forEach(hub => {
                lines.push(`- **${hub.name}**: ${hub.incoming} incoming, ${hub.outgoing} outgoing connections`);
            });
            lines.push('');
        }

        return lines.join('\n');
    }

    /**
     * Generate text boxes section
     */
    private generateTextBoxesSection(model: Model): string {
        const textBoxes = this.getTextBoxes(model);

        if (textBoxes.length === 0) {
            return '';
        }

        const lines: string[] = ['## Text Annotations'];
        lines.push('');
        lines.push('### Additional Notes');
        lines.push('');

        textBoxes.forEach((textBox, index) => {
            const text = textBox.content || '';
            if (text) {
                lines.push(`${index + 1}. ${text}`);
            }
        });

        return lines.join('\n');
    }

    /**
     * Generate views section
     */
    private generateViewsSection(model: Model): string {
        const views = model.views || [];

        if (views.length <= 1) {
            return '';
        }

        const lines: string[] = ['## Views'];
        lines.push('');
        lines.push('This diagram includes multiple views for different perspectives:');
        lines.push('');
        lines.push('| # | View Name | Components | Connections | Groups | Notes |');
        lines.push('|---|-----------|------------|-------------|--------|-------|');

        views.forEach((view, index) => {
            const name = view.name || `View ${index + 1}`;
            const items = (view.items || []).length;
            const connectors = (view.connectors || []).length;
            const rectangles = (view.rectangles || []).length;
            const textBoxes = (view.textBoxes || []).length;

            lines.push(`| ${index + 1} | **${name}** | ${items} | ${connectors} | ${rectangles} | ${textBoxes} annotations |`);
        });

        return lines.join('\n');
    }

    /**
     * Generate icons section
     */
    private generateIconsSection(model: Model): string {
        const items = model.items || [];

        // Count icon usage
        const iconUsage: Record<string, number> = {};
        items.forEach(item => {
            if (item.icon) {
                iconUsage[item.icon] = (iconUsage[item.icon] || 0) + 1;
            }
        });

        const lines: string[] = ['## Icon Library'];
        lines.push('');
        lines.push('### Icons Used in This Diagram');
        lines.push('');
        lines.push('| Icon | Name | Collection | Usage Count |');
        lines.push('|------|------|------------|-------------|');

        const usedIcons = Object.keys(iconUsage);
        usedIcons.forEach(iconId => {
            const iconDef = iconPackManager.getIconById(iconId);
            const name = iconDef?.name || iconId;
            const collection = iconDef?.pack || 'Unknown';
            const usage = iconUsage[iconId];

            lines.push(`| \`${iconId}\` | ${name} | ${collection} | ${usage} |`);
        });

        return lines.join('\n');
    }

    /**
     * Generate colors section
     */
    private generateColorsSection(model: Model): string {
        const colors = model.colors || [];
        const items = model.items || [];

        // Count color usage
        const colorUsage: Record<string, number> = {};
        items.forEach(item => {
            if (item.color) {
                colorUsage[item.color] = (colorUsage[item.color] || 0) + 1;
            }
        });

        const lines: string[] = ['## Color Palette'];
        lines.push('');
        lines.push('### Colors Used in This Diagram');
        lines.push('');
        lines.push('| Color | Hex Code | Usage Count |');
        lines.push('|-------|----------|-------------|');

        // Show palette colors
        colors.forEach(color => {
            const usage = colorUsage[color.value] || 0;
            lines.push(`| ![${color.value}](https://via.placeholder.com/20/${color.value.replace('#', '')}/000000?text=+) | \`${color.value}\` | ${usage} |`);
        });

        // Show additional colors used but not in palette
        const usedColors = Object.keys(colorUsage);
        const paletteColors = colors.map(c => c.value);
        const additionalColors = usedColors.filter(c => !paletteColors.includes(c));

        if (additionalColors.length > 0) {
            lines.push('');
            lines.push('### Additional Colors');
            lines.push('');
            additionalColors.forEach(color => {
                const usage = colorUsage[color];
                lines.push(`| ![${color}](https://via.placeholder.com/20/${color.replace('#', '')}/000000?text=+) | \`${color}\` | ${usage} |`);
            });
        }

        return lines.join('\n');
    }

    /**
     * Generate metadata section
     */
    private generateMetadataSection(model: Model): string {
        const lines: string[] = ['## Technical Metadata'];
        lines.push('');
        lines.push('| Property | Value |');
        lines.push('|----------|-------|');
        lines.push(`| **Diagram Title** | ${model.title || 'Untitled'} |`);
        lines.push(`| **Version** | ${model.version || 'N/A'} |`);
        lines.push(`| **Description** | ${model.description || '*None*'} |`);
        lines.push(`| **Total Components** | ${(model.items || []).length} |`);
        lines.push(`| **Total Connections** | ${this.getConnectors(model).length} |`);
        lines.push(`| **Total Groups** | ${this.getRectangles(model).length} |`);
        lines.push(`| **Total Views** | ${(model.views || []).length} |`);
        lines.push(`| **Total Icons** | ${(model.icons || []).length} |`);
        lines.push(`| **Total Colors** | ${(model.colors || []).length} |`);
        lines.push(`| **Export Date** | ${new Date().toISOString()} |`);
        lines.push(`| **Export Format** | Markdown Documentation |`);
        lines.push(`| **Generator Version** | ${this.APP_VERSION} |`);

        return lines.join('\n');
    }

    // ===== Helper Methods =====

    /**
     * Get all connectors from all views
     */
    private getConnectors(model: Model): Connector[] {
        const connectors: Connector[] = [];
        (model.views || []).forEach(view => {
            if (view.connectors) {
                connectors.push(...view.connectors);
            }
        });
        return connectors;
    }

    /**
     * Get all rectangles from all views
     */
    private getRectangles(model: Model): Rectangle[] {
        const rectangles: Rectangle[] = [];
        (model.views || []).forEach(view => {
            if (view.rectangles) {
                rectangles.push(...view.rectangles);
            }
        });
        return rectangles;
    }

    /**
     * Get all text boxes from all views
     */
    private getTextBoxes(model: Model): TextBox[] {
        const textBoxes: TextBox[] = [];
        (model.views || []).forEach(view => {
            if (view.textBoxes) {
                textBoxes.push(...view.textBoxes);
            }
        });
        return textBoxes;
    }

    /**
     * Get item name by ID
     */
    private getItemName(model: Model, itemId: string | undefined): string {
        if (!itemId) return '*Unknown*';
        const item = (model.items || []).find(i => i.id === itemId);
        return item?.name || `*Item ${itemId}*`;
    }

    /**
     * Get icon name by ID
     */
    private getIconName(iconId: string | undefined): string {
        if (!iconId) return '*None*';
        const iconDef = iconPackManager.getIconById(iconId);
        return iconDef?.name || iconId;
    }

    /**
     * Get connector endpoints from anchors
     */
    private getConnectorEndpoints(model: Model, connector: Connector): { source: string; target: string } {
        const anchors = connector.anchors || [];
        if (anchors.length < 2) {
            return { source: '*Unknown*', target: '*Unknown*' };
        }

        const sourceAnchor = anchors[0];
        const targetAnchor = anchors[anchors.length - 1];

        const sourceItemId = sourceAnchor.ref?.item;
        const targetItemId = targetAnchor.ref?.item;

        return {
            source: this.getItemName(model, sourceItemId),
            target: this.getItemName(model, targetItemId)
        };
    }

    /**
     * Analyze layers/zones in the architecture
     */
    private analyzeLayers(model: Model): string[] {
        const rectangles = this.getRectangles(model);
        return rectangles
            .map(r => r.description)
            .filter((name): name is string => !!name);
    }

    /**
     * Analyze data flow patterns
     */
    private analyzeDataFlow(model: Model, connectors: Connector[]): {
        entryPoints: string[];
        exitPoints: string[];
        hubs: Array<{ name: string; incoming: number; outgoing: number }>;
    } {
        const items = model.items || [];
        const itemIds = new Set(items.map(i => i.id));

        // Count incoming and outgoing connections
        const connectionCounts: Record<string, { incoming: number; outgoing: number }> = {};

        itemIds.forEach(id => {
            connectionCounts[id] = { incoming: 0, outgoing: 0 };
        });

        connectors.forEach(conn => {
            const { source, target } = this.getConnectorEndpoints(model, conn);

            // Find the item IDs from the names
            const sourceItem = items.find(i => i.name === source);
            const targetItem = items.find(i => i.name === target);

            if (sourceItem && connectionCounts[sourceItem.id]) {
                connectionCounts[sourceItem.id].outgoing++;
            }
            if (targetItem && connectionCounts[targetItem.id]) {
                connectionCounts[targetItem.id].incoming++;
            }
        });

        // Find entry points (no incoming)
        const entryPoints = Object.entries(connectionCounts)
            .filter(([_, counts]) => counts.incoming === 0 && counts.outgoing > 0)
            .map(([id, _]) => this.getItemName(model, id));

        // Find exit points (no outgoing)
        const exitPoints = Object.entries(connectionCounts)
            .filter(([_, counts]) => counts.outgoing === 0 && counts.incoming > 0)
            .map(([id, _]) => this.getItemName(model, id));

        // Find hubs (many connections)
        const hubs = Object.entries(connectionCounts)
            .filter(([_, counts]) => counts.incoming + counts.outgoing >= 3)
            .map(([id, counts]) => ({
                name: this.getItemName(model, id),
                incoming: counts.incoming,
                outgoing: counts.outgoing
            }))
            .sort((a, b) => (b.incoming + b.outgoing) - (a.incoming + a.outgoing));

        return { entryPoints, exitPoints, hubs };
    }

    /**
     * Infer system type from component names
     */
    private inferSystemType(items: ModelItem[]): string {
        const names = items.map(i => i.name?.toLowerCase() || '').join(' ');

        if (names.includes('microservice') || names.includes('service')) return 'Microservices';
        if (names.includes('lambda') || names.includes('function')) return 'Serverless';
        if (names.includes('api') && names.includes('database')) return 'API-driven';
        if (names.includes('pipeline') || names.includes('workflow')) return 'Data Pipeline';
        if (names.includes('queue') || names.includes('event')) return 'Event-Driven';

        return 'Component-Based';
    }

    /**
     * Identify key/critical components
     */
    private identifyKeyComponents(model: Model): ModelItem[] {
        const items = model.items || [];
        const connectors = this.getConnectors(model);
        const flowAnalysis = this.analyzeDataFlow(model, connectors);

        // Prioritize hubs and entry/exit points
        const keyComponentNames = new Set([
            ...flowAnalysis.entryPoints,
            ...flowAnalysis.exitPoints,
            ...flowAnalysis.hubs.map(h => h.name)
        ]);

        return items
            .filter(item => keyComponentNames.has(item.name || ''))
            .slice(0, 5); // Top 5 key components
    }

    /**
     * Analyze technology stack from component names
     */
    private analyzeTechnologyStack(model: Model): {
        categories: string[];
        technologies: Array<{ name: string; count: number }>;
    } {
        const items = model.items || [];
        const iconNames: Record<string, number> = {};
        const categories = new Set<string>();

        items.forEach(item => {
            if (item.icon) {
                const iconDef = iconPackManager.getIconById(item.icon);
                if (iconDef) {
                    iconNames[iconDef.name] = (iconNames[iconDef.name] || 0) + 1;

                    // Categorize by pack
                    const pack = iconDef.pack?.toLowerCase() || '';
                    if (pack.includes('aws')) categories.add('AWS');
                    else if (pack.includes('azure')) categories.add('Azure');
                    else if (pack.includes('gcp')) categories.add('Google Cloud');
                    else if (pack.includes('kubernetes')) categories.add('Kubernetes');
                }
            }
        });

        const technologies = Object.entries(iconNames)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);

        return {
            categories: Array.from(categories),
            technologies
        };
    }

    /**
     * Generate request flow description
     */
    private generateRequestFlow(model: Model, entryPoint: string): string {
        const connectors = this.getConnectors(model);
        const items = model.items || [];

        // Find the entry point item
        const entryItem = items.find(i => i.name === entryPoint);
        if (!entryItem) return '*Flow diagram to be documented*';

        // Trace connections from entry point
        const flow: string[] = [`1. **${entryPoint}** receives the initial request`];
        let currentId = entryItem.id;
        let step = 2;
        const visited = new Set<string>([currentId]);

        while (step < 10) { // Max 10 steps to avoid infinite loops
            const nextConnector = connectors.find(conn => {
                const { source } = this.getConnectorEndpoints(model, conn);
                const sourceItem = items.find(i => i.name === source);
                return sourceItem?.id === currentId && !visited.has(conn.id);
            });

            if (!nextConnector) break;

            const { target } = this.getConnectorEndpoints(model, nextConnector);
            const targetItem = items.find(i => i.name === target);
            if (!targetItem) break;

            flow.push(`${step}. **${target}** processes the request`);
            visited.add(nextConnector.id);
            currentId = targetItem.id;
            step++;
        }

        return flow.join('\n');
    }

    /**
     * Generate Mermaid diagrams section
     */
    private generateMermaidDiagrams(model: Model): string {
        const sections: string[] = ['## Interactive Diagrams'];
        sections.push('');
        sections.push('*The following diagrams are interactive and will render in GitHub, GitLab, and other Markdown viewers that support Mermaid.*');
        sections.push('');

        // 1. Logical Grouping Diagram (shows hierarchy)
        const extendedModel = model as ExtendedModel;
        const hierarchy = extendedModel.groups || this.getRectangles(model).length > 0;
        if (hierarchy) {
            const logicalGrouping = this.generateMermaidLogicalGrouping(model);
            if (logicalGrouping) {
                sections.push(logicalGrouping);
                sections.push('');
            }
        }

        // 2. C4 Context Diagram
        const c4Context = this.generateMermaidC4Context(model);
        if (c4Context) {
            sections.push(c4Context);
            sections.push('');
        }

        // 3. Component Relationship Graph
        const componentGraph = this.generateMermaidComponentGraph(model);
        if (componentGraph) {
            sections.push(componentGraph);
            sections.push('');
        }

        // 4. Sequence Diagram (if entry points exist)
        const sequenceDiagram = this.generateMermaidSequenceDiagram(model);
        if (sequenceDiagram) {
            sections.push(sequenceDiagram);
            sections.push('');
        }

        // 5. Deployment Diagram (if zones exist)
        if (this.getRectangles(model).length > 0) {
            const deploymentDiagram = this.generateMermaidDeployment(model);
            if (deploymentDiagram) {
                sections.push(deploymentDiagram);
                sections.push('');
            }
        }

        return sections.filter(s => s.length > 0).join('\n');
    }

    /**
     * Generate Mermaid Logical Grouping Diagram
     */
    private generateMermaidLogicalGrouping(model: Model): string {
        const extendedModel = model as ExtendedModel;
        const hierarchy = this.buildHierarchy(extendedModel);

        const lines: string[] = ['### Logical Grouping & Boundaries'];
        lines.push('');
        lines.push('*Shows how components are organized into zones and groups*');
        lines.push('');
        lines.push('```mermaid');
        lines.push('graph TB');
        lines.push('    %% Hierarchical Organization');
        lines.push('');

        let compIdx = 0;

        // Rectangles with groups (handle hierarchical nesting)
        hierarchy.rectangles.forEach((rect, rectIdx) => {
            const zoneName = rect.rectangle.description || `Zone ${rectIdx + 1}`;
            lines.push(`    subgraph R${rectIdx}["📦 ${zoneName}"]`);
            lines.push(`        direction TB`);

            // Build group tree for this rectangle's groups
            const rectGroupTree = this.buildGroupTree(rect.groups.map(g => g.group));
            const rootGroups = rectGroupTree.get('root') || [];

            // Recursively render nested groups
            const renderNestedGroup = (group: ElementGroup, depth: number, parentPrefix: string) => {
                const groupData = rect.groups.find(g => g.group.id === group.id);
                if (!groupData) return;

                const indent = '    '.repeat(depth + 1);
                const groupName = group.name;
                const groupId = `${parentPrefix}_${group.id.substring(0, 8)}`;

                lines.push(`${indent}subgraph ${groupId}["👥 ${groupName}"]`);
                lines.push(`${indent}    direction LR`);

                // Render child groups first
                const children = rectGroupTree.get(group.id) || [];
                children.forEach(child => renderNestedGroup(child, depth + 1, groupId));

                // Render items in this group
                groupData.items.forEach(item => {
                    const name = item.name || `C${compIdx}`;
                    lines.push(`${indent}    C${compIdx}["${name}"]`);
                    compIdx++;
                });

                lines.push(`${indent}end`);
            };

            // Render root-level groups
            rootGroups.forEach(group => renderNestedGroup(group, 0, `G${rectIdx}`));

            // Ungrouped items
            if (rect.ungroupedItems.length > 0) {
                rect.ungroupedItems.forEach(item => {
                    const name = item.name || `C${compIdx}`;
                    lines.push(`        C${compIdx}["${name}"]`);
                    compIdx++;
                });
            }

            lines.push(`    end`);
            lines.push('');
        });

        // Orphan groups (handle hierarchical nesting)
        if (hierarchy.orphanGroups.length > 0) {
            lines.push(`    subgraph Ungrouped["Independent Groups"]`);

            const orphanGroupTree = this.buildGroupTree(hierarchy.orphanGroups.map(g => g.group));
            const rootOrphanGroups = orphanGroupTree.get('root') || [];

            const renderNestedOrphanGroup = (group: ElementGroup, depth: number, parentPrefix: string) => {
                const groupData = hierarchy.orphanGroups.find(g => g.group.id === group.id);
                if (!groupData) return;

                const indent = '    '.repeat(depth + 2);
                const groupName = group.name;
                const groupId = `${parentPrefix}_${group.id.substring(0, 8)}`;

                lines.push(`${indent}subgraph ${groupId}["👥 ${groupName}"]`);
                lines.push(`${indent}    direction LR`);

                // Render child groups first
                const children = orphanGroupTree.get(group.id) || [];
                children.forEach(child => renderNestedOrphanGroup(child, depth + 1, groupId));

                // Render items in this group
                groupData.items.forEach(item => {
                    const name = item.name || `C${compIdx}`;
                    lines.push(`${indent}    C${compIdx}["${name}"]`);
                    compIdx++;
                });

                lines.push(`${indent}end`);
            };

            rootOrphanGroups.forEach(group => renderNestedOrphanGroup(group, 0, 'OG'));

            lines.push(`    end`);
            lines.push('');
        }

        // Orphan items
        if (hierarchy.orphanItems.length > 0) {
            lines.push(`    %% Standalone Components`);
            hierarchy.orphanItems.forEach(item => {
                const name = item.name || `C${compIdx}`;
                lines.push(`    C${compIdx}["${name}"]:::standalone`);
                compIdx++;
            });
            lines.push('');
        }

        // Styles
        lines.push('    %% Styles');
        lines.push('    classDef standalone fill:#fff9c4,stroke:#f57f17,stroke-width:2px,stroke-dasharray: 5 5');

        lines.push('```');

        return lines.join('\n');
    }

    /**
     * Generate Mermaid C4 Context diagram
     */
    private generateMermaidC4Context(model: Model): string {
        const items = model.items || [];
        const flowAnalysis = this.analyzeDataFlow(model, this.getConnectors(model));

        if (items.length === 0) return '';

        const lines: string[] = ['### C4 Context Diagram'];
        lines.push('');
        lines.push('```mermaid');
        lines.push('graph TB');
        lines.push('    %% System Boundary');

        // Add entry points as external systems
        flowAnalysis.entryPoints.forEach((entry, i) => {
            lines.push(`    ext_in_${i}[${entry}]:::external`);
        });

        // Add main system components
        items.slice(0, 10).forEach((item, i) => {
            const name = item.name || `Component ${i}`;
            lines.push(`    comp_${i}[${name}]:::component`);
        });

        // Add exit points as external systems
        flowAnalysis.exitPoints.forEach((exit, i) => {
            lines.push(`    ext_out_${i}[${exit}]:::external`);
        });

        // Add connections
        lines.push('');
        lines.push('    %% Connections');

        const connectors = this.getConnectors(model);
        connectors.slice(0, 20).forEach(conn => {
            const { source, target } = this.getConnectorEndpoints(model, conn);
            const sourceIdx = items.findIndex(i => i.name === source);
            const targetIdx = items.findIndex(i => i.name === target);

            if (sourceIdx >= 0 && targetIdx >= 0 && sourceIdx < 10 && targetIdx < 10) {
                const label = conn.description ? `|${conn.description}|` : '';
                const arrow = this.getMermaidArrow(conn);

                // Handle backward arrows
                if (conn.arrowDirection === 'backward') {
                    lines.push(`    comp_${targetIdx} ${arrow}${label} comp_${sourceIdx}`);
                } else {
                    lines.push(`    comp_${sourceIdx} ${arrow}${label} comp_${targetIdx}`);
                }
            }
        });

        // Add styles
        lines.push('');
        lines.push('    %% Styles');
        lines.push('    classDef external fill:#e1f5ff,stroke:#01579b,stroke-width:2px');
        lines.push('    classDef component fill:#fff9c4,stroke:#f57f17,stroke-width:2px');
        lines.push('```');

        return lines.join('\n');
    }

    /**
     * Generate Mermaid Component Graph
     */
    private generateMermaidComponentGraph(model: Model): string {
        const items = model.items || [];
        const connectors = this.getConnectors(model);

        if (items.length === 0) return '';

        const lines: string[] = ['### Component Relationship Diagram'];
        lines.push('');
        lines.push('```mermaid');
        lines.push('graph LR');
        lines.push('    %% Components');

        // Add all components (limit to 15 for readability)
        items.slice(0, 15).forEach((item, i) => {
            const name = item.name || `Component ${i}`;
            const iconInfo = item.icon ? this.getIconName(item.icon) : '';
            const label = iconInfo ? `${name}<br/><i>${iconInfo}</i>` : name;
            lines.push(`    C${i}["${label}"]`);
        });

        // Add connections with descriptions
        lines.push('');
        lines.push('    %% Relationships');

        connectors.slice(0, 30).forEach(conn => {
            const { source, target } = this.getConnectorEndpoints(model, conn);
            const sourceIdx = items.findIndex(i => i.name === source);
            const targetIdx = items.findIndex(i => i.name === target);

            if (sourceIdx >= 0 && targetIdx >= 0 && sourceIdx < 15 && targetIdx < 15) {
                const label = conn.description || '';
                const arrow = this.getMermaidArrow(conn);

                // Handle backward arrows
                const fromIdx = conn.arrowDirection === 'backward' ? targetIdx : sourceIdx;
                const toIdx = conn.arrowDirection === 'backward' ? sourceIdx : targetIdx;

                if (label) {
                    lines.push(`    C${fromIdx} ${arrow}|${label}| C${toIdx}`);
                } else {
                    lines.push(`    C${fromIdx} ${arrow} C${toIdx}`);
                }
            }
        });

        lines.push('```');

        return lines.join('\n');
    }

    /**
     * Generate Mermaid Sequence Diagram
     */
    private generateMermaidSequenceDiagram(model: Model): string {
        const flowAnalysis = this.analyzeDataFlow(model, this.getConnectors(model));
        const connectors = this.getConnectors(model);
        const items = model.items || [];

        if (flowAnalysis.entryPoints.length === 0 || connectors.length === 0) return '';

        const lines: string[] = ['### Request Flow Sequence'];
        lines.push('');
        lines.push('```mermaid');
        lines.push('sequenceDiagram');

        // Define participants
        const entryPoint = flowAnalysis.entryPoints[0];
        const entryItem = items.find(i => i.name === entryPoint);

        if (!entryItem) return '';

        lines.push(`    participant Client`);

        // Collect all components in the flow path
        const flowComponents = new Set<string>([entryPoint]);
        let currentId = entryItem.id;
        const visited = new Set<string>([currentId]);
        let maxSteps = 8; // Limit sequence diagram length

        // Trace the flow
        const flowSteps: Array<{ from: string; to: string; label: string }> = [];
        flowSteps.push({ from: 'Client', to: entryPoint, label: 'Initial Request' });

        while (maxSteps > 0) {
            const nextConnector = connectors.find(conn => {
                const { source } = this.getConnectorEndpoints(model, conn);
                const sourceItem = items.find(i => i.name === source);
                return sourceItem?.id === currentId && !visited.has(conn.id);
            });

            if (!nextConnector) break;

            const { source, target } = this.getConnectorEndpoints(model, nextConnector);
            const targetItem = items.find(i => i.name === target);
            if (!targetItem) break;

            flowComponents.add(target);
            flowSteps.push({
                from: source,
                to: target,
                label: nextConnector.description || 'Process'
            });

            visited.add(nextConnector.id);
            currentId = targetItem.id;
            maxSteps--;
        }

        // Add participant declarations
        flowComponents.forEach(comp => {
            lines.push(`    participant ${this.sanitizeMermaidId(comp)} as ${comp}`);
        });

        lines.push('');

        // Add flow steps
        flowSteps.forEach(step => {
            const fromId = this.sanitizeMermaidId(step.from);
            const toId = this.sanitizeMermaidId(step.to);
            lines.push(`    ${fromId}->>${toId}: ${step.label}`);
        });

        // Add response flow (reverse)
        if (flowSteps.length > 1) {
            lines.push('');
            lines.push('    %% Response Flow');
            for (let i = flowSteps.length - 1; i >= 1; i--) {
                const step = flowSteps[i];
                const fromId = this.sanitizeMermaidId(step.to);
                const toId = this.sanitizeMermaidId(step.from);
                lines.push(`    ${fromId}-->>${toId}: Response`);
            }
        }

        lines.push('```');

        return lines.join('\n');
    }

    /**
     * Generate Mermaid Deployment Diagram
     */
    private generateMermaidDeployment(model: Model): string {
        const extendedModel = model as ExtendedModel;
        const hierarchy = this.buildHierarchy(extendedModel);

        if (hierarchy.rectangles.length === 0) return '';

        const lines: string[] = ['### Deployment Architecture'];
        lines.push('');
        lines.push('*Using actual spatial containment - components shown in their actual zones*');
        lines.push('');
        lines.push('```mermaid');
        lines.push('graph TB');
        lines.push('    %% Deployment Zones (based on actual containment)');
        lines.push('');

        const itemIdToIndex = new Map<string, number>();
        let globalIdx = 0;

        // Build zones with actual contained items
        hierarchy.rectangles.forEach((rect, zoneIdx) => {
            const zoneName = rect.rectangle.description || `Zone ${zoneIdx + 1}`;
            lines.push(`    subgraph Zone${zoneIdx}["📦 ${zoneName}"]`);

            // Add groups within this zone
            rect.groups.forEach((grp, grpIdx) => {
                const groupName = grp.group.name;
                const groupColor = grp.group.color || '#e3f2fd';
                lines.push(`        subgraph Group${zoneIdx}_${grpIdx}["👥 ${groupName}"]`);

                grp.items.forEach(item => {
                    itemIdToIndex.set(item.id, globalIdx);
                    const name = item.name || `Component ${globalIdx}`;
                    lines.push(`            C${globalIdx}["${name}"]`);
                    globalIdx++;
                });

                lines.push(`        end`);
                lines.push(`        style Group${zoneIdx}_${grpIdx} fill:${groupColor},stroke:#1976d2,stroke-width:2px`);
            });

            // Add ungrouped items
            rect.ungroupedItems.forEach(item => {
                itemIdToIndex.set(item.id, globalIdx);
                const name = item.name || `Component ${globalIdx}`;
                lines.push(`        C${globalIdx}["${name}"]`);
                globalIdx++;
            });

            lines.push('    end');
            lines.push('');
        });

        // Add orphan groups
        hierarchy.orphanGroups.forEach((grp, grpIdx) => {
            const groupName = grp.group.name;
            lines.push(`    subgraph OrphanGroup${grpIdx}["👥 ${groupName}"]`);

            grp.items.forEach(item => {
                itemIdToIndex.set(item.id, globalIdx);
                const name = item.name || `Component ${globalIdx}`;
                lines.push(`        C${globalIdx}["${name}"]`);
                globalIdx++;
            });

            lines.push('    end');
            lines.push('');
        });

        // Add orphan items
        hierarchy.orphanItems.forEach(item => {
            itemIdToIndex.set(item.id, globalIdx);
            const name = item.name || `Component ${globalIdx}`;
            lines.push(`    C${globalIdx}["${name}"]`);
            globalIdx++;
        });

        // Add connections
        const connectors = this.getConnectors(model);
        const items = model.items || [];

        if (connectors.length > 0) {
            lines.push('');
            lines.push('    %% Component Communications');

            connectors.slice(0, 30).forEach(conn => {
                const { source, target } = this.getConnectorEndpoints(model, conn);
                const sourceItem = items.find(i => i.name === source);
                const targetItem = items.find(i => i.name === target);

                if (sourceItem && targetItem) {
                    const sourceIdx = itemIdToIndex.get(sourceItem.id);
                    const targetIdx = itemIdToIndex.get(targetItem.id);

                    if (sourceIdx !== undefined && targetIdx !== undefined) {
                        const label = conn.description ? `|${conn.description}|` : '';
                        const arrow = this.getMermaidArrow(conn);

                        // Handle backward arrows
                        if (conn.arrowDirection === 'backward') {
                            lines.push(`    C${targetIdx} ${arrow} ${label} C${sourceIdx}`);
                        } else {
                            lines.push(`    C${sourceIdx} ${arrow} ${label} C${targetIdx}`);
                        }
                    }
                }
            });
        }

        lines.push('```');

        return lines.join('\n');
    }

    /**
     * Sanitize string for use as Mermaid ID
     */
    private sanitizeMermaidId(str: string): string {
        // Remove special characters and spaces, replace with underscores
        return str
            .replace(/[^a-zA-Z0-9]/g, '_')
            .replace(/^[0-9]/, '_$&') // Prefix with _ if starts with number
            .substring(0, 50); // Limit length
    }

    /**
     * Generate Mermaid arrow syntax based on connector properties
     */
    private getMermaidArrow(connector: Connector): string {
        const style = connector.style || 'SOLID';
        const arrowType = connector.arrowType || 'SINGLE';
        const arrowDirection = connector.arrowDirection || 'forward';

        // Base arrow patterns
        let arrow = '';

        if (style === 'DOTTED') {
            // Dotted line
            if (arrowDirection === 'backward') {
                arrow = arrowType === 'DOUBLE' ? '<-..-' : '<..-';
            } else {
                arrow = arrowType === 'DOUBLE' ? '-..->' : '..>';
            }
        } else if (style === 'DASHED') {
            // Dashed line
            if (arrowDirection === 'backward') {
                arrow = arrowType === 'DOUBLE' ? '<--' : '<--';
            } else {
                arrow = arrowType === 'DOUBLE' ? '<-->' : '-->';
            }
        } else {
            // Solid line (default)
            if (arrowDirection === 'backward') {
                arrow = arrowType === 'DOUBLE' ? '<--' : '<--';
            } else {
                arrow = arrowType === 'DOUBLE' ? '<-->' : '-->';
            }
        }

        // Handle NONE arrow type
        if (arrowType === 'NONE') {
            if (style === 'DOTTED') arrow = '-.-';
            else if (style === 'DASHED') arrow = '---';
            else arrow = '---';
        }

        return arrow;
    }

    /**
     * Get flow direction indicator for documentation
     */
    private getFlowIndicator(connector: Connector): string {
        if (!connector.flowEnabled) return '';

        const flowDir = connector.flowDirection || 'forward';
        const speed = connector.flowSpeed || 1;

        let indicator = '';
        if (flowDir === 'forward') indicator = '➡️';
        else if (flowDir === 'backward') indicator = '⬅️';
        else if (flowDir === 'bidirectional') indicator = '⬅️➡️';

        const speedDesc = speed > 2 ? ' (fast)' : speed < 1 ? ' (slow)' : '';

        return indicator ? ` ${indicator}${speedDesc}` : '';
    }

    /**
     * Determine if a point is inside a rectangle
     */
    private isPointInRectangle(point: { x: number; y: number }, rect: Rectangle): boolean {
        const minX = Math.min(rect.from.x, rect.to.x);
        const maxX = Math.max(rect.from.x, rect.to.x);
        const minY = Math.min(rect.from.y, rect.to.y);
        const maxY = Math.max(rect.from.y, rect.to.y);

        return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
    }

    /**
     * Get the rectangle that contains an item
     */
    private getContainingRectangle(itemId: string, view: View): Rectangle | undefined {
        const viewItem = view.items?.find(vi => vi.id === itemId);
        if (!viewItem) return undefined;

        const rectangles = view.rectangles || [];

        // Find the smallest rectangle that contains this item
        return rectangles
            .filter(rect => this.isPointInRectangle(viewItem.tile, rect))
            .sort((a, b) => {
                // Sort by area (smaller rectangles first)
                const areaA = Math.abs((a.to.x - a.from.x) * (a.to.y - a.from.y));
                const areaB = Math.abs((b.to.x - b.from.x) * (b.to.y - b.from.y));
                return areaA - areaB;
            })[0];
    }

    /**
     * Get the group that contains an item (returns the most nested/child group)
     */
    private getContainingGroup(itemId: string, groups: ElementGroup[]): ElementGroup | undefined {
        // Find all groups containing this item
        const containingGroups = groups.filter(group =>
            group.elements.some(el => el.type === 'ITEM' && el.id === itemId)
        );

        if (containingGroups.length === 0) return undefined;
        if (containingGroups.length === 1) return containingGroups[0];

        // If multiple groups contain the item, find the most nested one (child group)
        // A child group is one that has a parent
        const childGroup = containingGroups.find(g =>
            g.parentGroupId && containingGroups.some(parent => parent.id === g.parentGroupId)
        );

        return childGroup || containingGroups[0];
    }

    /**
     * Build group hierarchy tree (parent -> children)
     */
    private buildGroupTree(groups: ElementGroup[]): Map<string, ElementGroup[]> {
        const tree = new Map<string, ElementGroup[]>();

        groups.forEach(group => {
            const parentId = group.parentGroupId || 'root';
            if (!tree.has(parentId)) {
                tree.set(parentId, []);
            }
            tree.get(parentId)!.push(group);
        });

        return tree;
    }

    /**
     * Get all descendant groups of a parent group
     */
    private getDescendantGroups(parentId: string, groupTree: Map<string, ElementGroup[]>): ElementGroup[] {
        const descendants: ElementGroup[] = [];
        const children = groupTree.get(parentId) || [];

        children.forEach(child => {
            descendants.push(child);
            descendants.push(...this.getDescendantGroups(child.id, groupTree));
        });

        return descendants;
    }

    /**
     * Build hierarchical structure: Rectangle -> Group -> Items
     */
    private buildHierarchy(model: ExtendedModel): {
        rectangles: Array<{
            rectangle: Rectangle;
            groups: Array<{
                group: ElementGroup;
                items: ModelItem[];
            }>;
            ungroupedItems: ModelItem[];
        }>;
        orphanGroups: Array<{
            group: ElementGroup;
            items: ModelItem[];
        }>;
        orphanItems: ModelItem[];
    } {
        const items = model.items || [];
        const groups = model.groups || [];
        const views = model.views || [];
        const mainView = views[0]; // Use first view for spatial analysis

        if (!mainView) {
            // No views, return flat structure
            return {
                rectangles: [],
                orphanGroups: groups.map(group => ({
                    group,
                    items: items.filter(item =>
                        group.elements.some(el => el.type === 'ITEM' && el.id === item.id)
                    )
                })),
                orphanItems: items.filter(item =>
                    !groups.some(group =>
                        group.elements.some(el => el.type === 'ITEM' && el.id === item.id)
                    )
                )
            };
        }

        const rectangles = mainView.rectangles || [];
        const processedItems = new Set<string>();
        const processedGroups = new Set<string>();

        // Build rectangle hierarchy
        const rectangleHierarchy = rectangles.map(rectangle => {
            const rectangleItems = items.filter(item => {
                const containingRect = this.getContainingRectangle(item.id, mainView);
                return containingRect?.id === rectangle.id;
            });

            // Separate grouped and ungrouped items within this rectangle
            const groupsInRect: Array<{ group: ElementGroup; items: ModelItem[] }> = [];
            const ungroupedItems: ModelItem[] = [];

            rectangleItems.forEach(item => {
                const group = this.getContainingGroup(item.id, groups);
                if (group && !processedGroups.has(group.id)) {
                    // This item belongs to a group
                    const groupItems = items.filter(i =>
                        group.elements.some(el => el.type === 'ITEM' && el.id === i.id)
                    );
                    groupsInRect.push({ group, items: groupItems });
                    processedGroups.add(group.id);
                    groupItems.forEach(gi => processedItems.add(gi.id));
                } else if (!group && !processedItems.has(item.id)) {
                    ungroupedItems.push(item);
                    processedItems.add(item.id);
                }
            });

            return {
                rectangle,
                groups: groupsInRect,
                ungroupedItems
            };
        });

        // Find orphan groups (not in any rectangle)
        const orphanGroups = groups
            .filter(group => !processedGroups.has(group.id))
            .map(group => ({
                group,
                items: items.filter(item =>
                    group.elements.some(el => el.type === 'ITEM' && el.id === item.id)
                )
            }));

        orphanGroups.forEach(({ items: groupItems }) => {
            groupItems.forEach(item => processedItems.add(item.id));
        });

        // Find orphan items (not in any rectangle or group)
        const allOrphanItems = items.filter(item => !processedItems.has(item.id));

        // Filter out unnamed orphan items with no connectors
        const orphanItems = allOrphanItems.filter(item => {
            // Keep item if it has a name
            if (item.name && item.name.trim()) return true;

            // Check if item has any connectors
            const hasConnectors = this.getConnectors(model).some(conn => {
                const { source, target } = this.getConnectorEndpoints(model, conn);
                const sourceItem = items.find(i => i.name === source);
                const targetItem = items.find(i => i.name === target);
                return sourceItem?.id === item.id || targetItem?.id === item.id;
            });

            return hasConnectors;
        });

        return {
            rectangles: rectangleHierarchy,
            orphanGroups,
            orphanItems
        };
    }

}

export const createMarkdownExportHandler = (): MarkdownExportHandler => {
    return new MarkdownExportHandler();
};
